import React from "react";
import {
  FiEye,
  FiMessageSquare,
  FiTrash2,
  FiMapPin,
  FiImage,
  FiHome,
} from "react-icons/fi";
import { formatCurrency, formatDate, resolveAssetUrl } from "../utils/formatters";
import "./PropertyTableView.css";

const PropertyTableView = ({
  properties = [],
  onViewDetails,
  onOpenComments,
  onDelete,
  canDelete = false,
}) => {
  if (properties.length === 0) {
    return null;
  }

  return (
    <div className="shared-prop-table-wrap">
      <table className="shared-prop-table">
        <thead>
          <tr>
            <th>Property</th>
            <th>Type</th>
            <th>Price</th>
            <th>Location</th>
            <th>Stage / Purpose</th>
            <th>Listed By</th>
            <th>Date</th>
            <th className="shared-prop-table__actions-head">Actions</th>
          </tr>
        </thead>
        <tbody>
          {properties.map((property) => {
            const propImagesList = Array.isArray(property.propertyImages) ? property.propertyImages : [];
            const standardImagesList = Array.isArray(property.images) ? property.images : [];
            const allImages = propImagesList.length > 0 ? propImagesList : standardImagesList;
            const firstImg = allImages.length > 0 ? allImages[0] : null;
            const imgUrl = firstImg?.image_url || firstImg?.imagePath || firstImg?.url;
            const resolvedImg = imgUrl ? resolveAssetUrl(imgUrl) : null;

            const propertyTitle =
              property.project_name || property.property_name || property.title || `Property #${property.id}`;

            const propertyTypeName =
              typeof property.property_type === "object"
                ? property.property_type?.name
                : property.property_type || "N/A";

            const purposeName =
              typeof property.property_purpose === "object"
                ? property.property_purpose?.name
                : property.property_purpose || "";

            const stageName =
              typeof property.property_stage === "object"
                ? property.property_stage?.name
                : typeof property.property_status === "object"
                ? property.property_status?.name
                : property.property_stage || property.property_status || "";

            const ownerName =
              property.partner?.first_name || property.customer?.first_name || property.user?.first_name || property.customer_name
                ? `${property.partner?.first_name || property.customer?.first_name || property.user?.first_name || property.customer_name} ${
                    property.partner?.last_name || property.customer?.last_name || property.user?.last_name || ""
                  }`.trim()
                : "Partner";

            const propertyCost = property.cost || property.expected_cost || property.starting_cost || property.price;
            const formattedPrice = formatCurrency(propertyCost) || "On Request";
            const postDate = property.created_on || property.created_at;

            return (
              <tr key={property.id}>
                <td>
                  <div className="shared-prop-table__project-cell">
                    <div
                      className="shared-prop-table__thumb"
                      onClick={() => onViewDetails(property)}
                    >
                      {resolvedImg ? (
                        <img
                          src={resolvedImg}
                          alt={propertyTitle}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <FiImage className="shared-prop-table__thumb-placeholder" />
                      )}
                    </div>
                    <div className="shared-prop-table__project-info">
                      <span
                        className="shared-prop-table__project-name"
                        onClick={() => onViewDetails(property)}
                      >
                        {propertyTitle}
                      </span>
                      {property.area_name && (
                        <span className="shared-prop-table__project-sub">
                          {property.area_name}
                        </span>
                      )}
                    </div>
                  </div>
                </td>

                <td>
                  <span className="shared-prop-table__type-badge">
                    <FiHome size={12} />
                    <span>{propertyTypeName}</span>
                  </span>
                </td>

                <td>
                  <span className="shared-prop-table__cost">
                    {formattedPrice}
                  </span>
                </td>

                <td>
                  <div className="shared-prop-table__location-cell">
                    <FiMapPin size={13} className="text-danger" />
                    <span>
                      {[property.city_name, property.state_name].filter(Boolean).join(", ") ||
                        "N/A"}
                    </span>
                  </div>
                </td>

                <td>
                  <div className="d-flex flex-column gap-1">
                    {purposeName && (
                      <span className="shared-prop-table__purpose-tag">{purposeName}</span>
                    )}
                    {stageName && (
                      <span className="shared-prop-table__stage-tag">{stageName}</span>
                    )}
                  </div>
                </td>

                <td>
                  <span className="shared-prop-table__partner-name">{ownerName}</span>
                </td>

                <td>
                  <span className="shared-prop-table__date">
                    {formatDate(postDate)}
                  </span>
                </td>

                <td>
                  <div className="shared-prop-table__actions">
                    <button
                      type="button"
                      className="shared-prop-table__action-btn shared-prop-table__action-btn--primary"
                      onClick={() => onViewDetails(property)}
                      title="View Details"
                    >
                      <FiEye size={15} />
                    </button>

                    <button
                      type="button"
                      className="shared-prop-table__action-btn shared-prop-table__action-btn--secondary"
                      onClick={() => onOpenComments(property)}
                      title="Discussion"
                    >
                      <FiMessageSquare size={15} />
                    </button>

                    {canDelete && (
                      <button
                        type="button"
                        className="shared-prop-table__action-btn shared-prop-table__action-btn--danger"
                        onClick={() => onDelete(property)}
                        title="Delete Property"
                      >
                        <FiTrash2 size={15} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PropertyTableView;
